import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import useOutgoingTabQuery, {
  UseOutgoingTabQueryOptions,
} from "./useOutgoingTabQuery";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { ViewStateStatus } from "./viewState";
import OutgoingTabTable from "./TableView";
import { SHIFT_REQUEST_LIST_PAGE_SIZE } from "../ShiftRequestListScreen";
import Pagination from "oneclick-component/src/components/Pagination";
import cn from "classnames";
import useShowMessage from "oneclick-component/src/hooks/useShowMessage";
import OutgoingTabActionBar from "./ActionBar";
import { usePtUserRoleFilterOptions } from "../../ShiftListScreen/usePtUserRoleFilterOptions";
import useShiftRateFilterOptions from "../../ShiftListScreen/useShiftRateFilterOptions";
import {
  ShiftDateRangeFilter,
  getDateRangeFromFilter,
} from "../../ShiftListScreen/DateRangeFilter/model";
import useOnClickTableColumnHeader, {
  ControlledTableState,
} from "oneclick-component/src/hooks/useOnClickTableColumnHeader";
import useSortingQueryParam from "./useSortingQueryParam";
import { OutgoingTabRecord } from "./model";
import {
  Station,
  useExportShiftRequestHandlerShiftRequestsExportPostMutation as useExportShiftRequest,
} from "oneclick-component/src/store/apis/enhancedApi";
import { FilterProps } from "oneclick-component/src/models";
import useMeUser from "../../../hooks/useMeUser";
import useMeUserStations from "../../../hooks/useMeUserStations";
import {
  saveOutgoingPtTabFilter,
  OutgoingPtTabFilterState,
} from "../../../store/filterState";
import { RootState } from "../../../store/store";

interface OutgoingTabFilterState {
  shiftDateRange: ShiftDateRangeFilter;
  ptRole: string | null;
  shiftRate: number | null;
  searchString?: string;
  pageIndex: number;
  pageSize: number;
  workingStationId: number | null;
  ptStationId?: number | null; // only applies to Superadmin/Landlord
}

const DEFAULT_FILTER_STATE: OutgoingTabFilterState = {
  shiftDateRange: { type: "all" },
  ptRole: null,
  shiftRate: null,
  searchString: undefined,
  pageIndex: 0,
  pageSize: SHIFT_REQUEST_LIST_PAGE_SIZE,
  workingStationId: null,
};

const getDefaultFilterState = (
  savedFilter?: OutgoingPtTabFilterState
): OutgoingTabFilterState => {
  return {
    ...DEFAULT_FILTER_STATE,
    ...savedFilter,
  };
};

const OutgoingPartTimeTab = (): ReactElement => {
  const showMessage = useShowMessage();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const savedFilter = useSelector((state: RootState) => {
    return state.filterState.outgoingPtTabFilter;
  }, shallowEqual);

  const [ptUserRoleFilterOptions, resolvePtUserRoleOptionToRoleId] =
    usePtUserRoleFilterOptions();
  const shiftRateFilterOptions = useShiftRateFilterOptions();
  const meUser = useMeUser();
  const [controlledTableState, setControlledTableState] =
    useState<ControlledTableState>({
      sorting: [{ id: "shiftDutyStartTime", desc: false }],
      grouping: ["shiftDate"],
    });
  const sortingQueryParam = useSortingQueryParam(controlledTableState.sorting);
  const [exportShiftRequest] = useExportShiftRequest();

  const meStations: Station[] | "all" = useMeUserStations();
  const ptStationIds = useMemo<number[] | null>(() => {
    if (meStations === "all" || meStations.length === 0) {
      return null;
    }
    return meStations.map((s) => s.id);
  }, [meStations]);

  const [filterState, setFilterState] = useState<OutgoingTabFilterState>(
    getDefaultFilterState(savedFilter)
  );
  const queryOptions = useMemo<UseOutgoingTabQueryOptions>(() => {
    const shiftDateRange = getDateRangeFromFilter(filterState.shiftDateRange);
    return {
      isOut: true,
      pageIndex: filterState.pageIndex,
      pageSize: filterState.pageSize,
      shiftAfter: shiftDateRange?.after?.toISO(),
      shiftBefore: shiftDateRange?.before?.toISO(),
      shiftSearchString: filterState.searchString,
      shiftRateId:
        filterState.shiftRate != null ? [filterState.shiftRate] : null,
      ptRoleId: resolvePtUserRoleOptionToRoleId(filterState.ptRole),
      sort: sortingQueryParam,
      shiftWorkingStationId:
        filterState.workingStationId != null
          ? [filterState.workingStationId]
          : null,
      ptStationId:
        filterState.ptStationId != null ? [filterState.ptStationId] : null,
    };
  }, [
    filterState.pageIndex,
    filterState.pageSize,
    filterState.ptRole,
    filterState.ptStationId,
    filterState.searchString,
    filterState.shiftDateRange,
    filterState.shiftRate,
    filterState.workingStationId,
    sortingQueryParam,
    resolvePtUserRoleOptionToRoleId,
  ]);

  useEffect(() => {
    scrollTo({ top: 0 });
  }, [filterState, sortingQueryParam]);

  const viewState = useOutgoingTabQuery(queryOptions);
  const onChangeShiftDateRange = useCallback(
    (shiftDateRange: ShiftDateRangeFilter) => {
      dispatch(
        saveOutgoingPtTabFilter({
          shiftDateRange,
        })
      );
      setFilterState((prev) => ({ ...prev, pageIndex: 0, shiftDateRange }));
    },
    [dispatch]
  );
  const onChangePtRole = useCallback(
    (ptRole: string | null) => {
      dispatch(
        saveOutgoingPtTabFilter({
          ptRole,
        })
      );
      setFilterState((prev) => ({ ...prev, pageIndex: 0, ptRole }));
    },
    [dispatch]
  );
  const onChangeShiftRate = useCallback(
    (shiftRate: number | null) => {
      dispatch(
        saveOutgoingPtTabFilter({
          shiftRate,
        })
      );
      setFilterState((prev) => ({ ...prev, pageIndex: 0, shiftRate }));
    },
    [dispatch]
  );
  const onChangeSearchString = useCallback(
    (v: string | null) => {
      if (v == null || v.trim() === "") {
        dispatch(
          saveOutgoingPtTabFilter({
            searchString: undefined,
          })
        );
        setFilterState((prev) => ({
          ...prev,
          pageIndex: 0,
          searchString: undefined,
        }));
        return;
      }
      dispatch(
        saveOutgoingPtTabFilter({
          searchString: v,
        })
      );
      setFilterState((prev) => ({ ...prev, pageIndex: 0, searchString: v }));
    },
    [dispatch]
  );
  const onChangeWorkingStation = useCallback(
    (workingStationId: number | null) => {
      dispatch(
        saveOutgoingPtTabFilter({
          workingStationId: workingStationId,
        })
      );
      setFilterState((prev) => ({ ...prev, pageIndex: 0, workingStationId }));
    },
    [dispatch]
  );
  const onChangePtStation = useCallback(
    (stationId: number | null) => {
      dispatch(
        saveOutgoingPtTabFilter({
          ptStationId: stationId,
        })
      );
      setFilterState((prev) => ({
        ...prev,
        pageIndex: 0,
        ptStationId: stationId,
      }));
    },
    [dispatch]
  );
  const ptStationFilter = useMemo<
    FilterProps<number | null> | undefined
  >(() => {
    if (meUser == null || meUser.adminRole === "MANAGER") {
      return undefined;
    }
    return {
      value: filterState.ptStationId ?? null,
      onChange: onChangePtStation,
    };
  }, [filterState.ptStationId, meUser, onChangePtStation]);

  const handleOnClickPage = useCallback((pageIndex: number) => {
    setFilterState((prev) => ({ ...prev, pageIndex }));
  }, []);

  const onClickColumnHeader = useOnClickTableColumnHeader<OutgoingTabRecord>(
    setControlledTableState,
    undefined,
    undefined,
    new Set<string>(["shiftDate"])
  );

  const handleExport = useCallback(() => {
    exportShiftRequest({
      ...queryOptions,
      shiftStatus: ["active", "expired"],
      shiftRequestStatus: ["hired"],
      ptStationId: queryOptions.ptStationId ?? ptStationIds,
    })
      .unwrap()
      .then(() => {
        showMessage({
          type: "info",
          title: t("shiftRequestList.export.started"),
        });
      })
      .catch(() => {
        showMessage({
          type: "fail",
          title: t("shiftRequestList.export.failed"),
        });
      });
  }, [queryOptions, ptStationIds, exportShiftRequest, showMessage, t]);

  const isViewStateLoaded = viewState.status === ViewStateStatus.Loaded;
  return (
    <>
      <OutgoingTabActionBar
        shiftDateRangeFilter={{
          value: filterState.shiftDateRange,
          onChange: onChangeShiftDateRange,
        }}
        ptRoleFilter={{
          value: filterState.ptRole,
          onChange: onChangePtRole,
          options: ptUserRoleFilterOptions,
        }}
        shiftRateFilter={{
          value: filterState.shiftRate,
          onChange: onChangeShiftRate,
          options: shiftRateFilterOptions,
        }}
        workingStationFilter={{
          value: filterState.workingStationId,
          onChange: onChangeWorkingStation,
        }}
        ptStationFilter={ptStationFilter}
        searchString={filterState.searchString}
        onChangeSearchString={onChangeSearchString}
        onExport={handleExport}
        className="mb-6"
      />
      <OutgoingTabTable
        viewState={viewState}
        tableState={controlledTableState}
        onClickColumnHeader={onClickColumnHeader}
        isHidePtStationColumn={meUser?.adminRole === "MANAGER"}
      />
      <Pagination
        className={cn(
          "mt-3",
          "bg-white",
          "fixed",
          "bottom-0",
          "inset-x-0",
          "sm:static"
        )}
        totalPages={
          isViewStateLoaded
            ? Math.ceil(viewState.total / SHIFT_REQUEST_LIST_PAGE_SIZE)
            : 0
        }
        pageIndex={isViewStateLoaded ? viewState.pageIndex : 0}
        onClickPage={handleOnClickPage}
      />
      <div className="h-16" />
    </>
  );
};

export default OutgoingPartTimeTab;
